<template>
  <div class="report-filter-dropdown">
    <SrpDropdown placement="bottom-end" :offset="3">
      <template #clickableElement="{ isDropdownVisible }">
        <div class="report-filter-dropdown__trigger">
          <LinkWithIcon isDottedUnderline color="gray">{{ props.label ? `${props.label}: ${selectedLabel}` : selectedLabel }}</LinkWithIcon>
          <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" color="#5b5b5b" style="margin-left: 2px" />
        </div>
      </template>
      <template #dropdown="{ close }">
        <div class="report-filter-dropdown__dropdown">
          <div
            v-for="option in props.options"
            :key="option.value"
            class="report-filter-dropdown__option"
            :class="{ 'report-filter-dropdown__option--selected': option.value === props.modelValue }"
            @click="selectOption(option.value, close)"
          >
            <span>{{ option.label }}</span>
            <IconEmbedded v-if="option.value === props.modelValue" name="check_3" :size="16" color="rgba(0, 128, 96, 1)" />
          </div>
        </div>
      </template>
    </SrpDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";

export interface ReportFilterOption {
  value: string;
  label: string;
}

const props = defineProps<{
  /** Prefix shown before the selected option ("Dates: Ever"); omit when the option labels read fine alone. */
  label?: string;
  options: ReportFilterOption[];
  modelValue: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const selectedLabel = computed<string>(() => props.options.find(o => o.value === props.modelValue)?.label ?? "");

function selectOption(value: string, closeDropdown: () => void) {
  if (value !== props.modelValue) emit("update:modelValue", value);
  closeDropdown();
}
</script>

<style scoped lang="scss">
// Report filter dropdown =====================================================
.report-filter-dropdown {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 18px;
    cursor: pointer;

    :deep(.embedded-icon) {
      flex-shrink: 0;
      line-height: 0;
    }
  }

  &__dropdown {
    min-width: 170px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    background: white;
  }

  &__option {
    gap: 12px;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: rgba(0, 128, 128, 0.06);
    }

    &--selected {
      background-color: rgba(0, 128, 128, 0.08);
    }
  }
}
</style>
